import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Link2,
  Unlink,
  CheckCircle,
  AlertCircle,
  Loader2,
  Sparkles,
  Info
} from 'lucide-react'
import { getGoCardlessMappings, unlinkGoCardlessMandate } from '../../supabase-client'
import GoCardlessMatchingModal from '../GoCardlessMatchingModal'
import { toast } from 'sonner'

/**
 * GoCardlessMandateMapping Component
 *
 * Manages linking Odin customers to GoCardless mandates.
 * Features:
 * - Summary statistics (total, mapped, unmapped, percentage)
 * - List unmapped customers with fuzzy match suggestions
 * - List mapped customers with mandate details
 * - Link/unlink functionality
 *
 * @param {Object} props
 * @param {Object} props.user - Current authenticated user
 */
export default function GoCardlessMandateMapping({ user }) {
  const [mappings, setMappings] = useState([])
  const [unmappedCustomers, setUnmappedCustomers] = useState([])
  const [suggestions, setSuggestions] = useState({})
  const [summary, setSummary] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // Link modal state
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)

  // Load mappings on mount
  useEffect(() => {
    fetchMappings()
  }, [])

  /**
   * Fetch all customer mappings with suggestions
   */
  const fetchMappings = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const data = await getGoCardlessMappings(true, true)

      setMappings(data.mappings || [])
      setUnmappedCustomers(data.unmapped || [])
      setSuggestions(data.suggestions || {})
      setSummary(data.summary || {})

      console.log('[GoCardlessMandateMapping] Loaded mappings:', {
        mapped: data.mappings?.length || 0,
        unmapped: data.unmapped?.length || 0,
        suggestions: Object.keys(data.suggestions || {}).length
      })
    } catch (error) {
      console.error('[GoCardlessMandateMapping] Error fetching mappings:', error)
      setError(error.message || 'Failed to load mappings')
      toast.error('Failed to load mappings', {
        description: error.message
      })
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Open link modal for a customer
   */
  const handleLinkCustomer = (customer) => {
    setSelectedCustomer(customer)
    setIsLinkModalOpen(true)
  }

  /**
   * Handle successful link
   */
  const handleLinkSuccess = () => {
    setIsLinkModalOpen(false)
    setSelectedCustomer(null)
    fetchMappings() // Refresh data
  }

  /**
   * Unlink customer from GoCardless mandate
   */
  const handleUnlinkCustomer = async (customerId, customerName) => {
    const confirmed = window.confirm(
      `Are you sure you want to unlink ${customerName} from their GoCardless mandate?\n\nThis will remove the Direct Debit payment method association.`
    )

    if (!confirmed) return

    try {
      await unlinkGoCardlessMandate(customerId)

      toast.success('Customer unlinked', {
        description: `${customerName} has been unlinked from GoCardless`
      })

      fetchMappings() // Refresh data
    } catch (error) {
      console.error('[GoCardlessMandateMapping] Unlink error:', error)
      toast.error('Failed to unlink customer', {
        description: error.message
      })
    }
  }

  /**
   * Get mandate status badge variant
   */
  const getMandateStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'pending_customer_approval':
      case 'pending_submission':
        return 'warning'
      case 'failed':
      case 'cancelled':
      case 'expired':
        return 'destructive'
      default:
        return 'secondary'
    }
  }

  /**
   * Format mandate status for display
   */
  const formatMandateStatus = (status) => {
    if (!status) return 'Unknown'
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-semibold">GoCardless Mandate Mapping</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Link Odin customers to GoCardless mandates for Direct Debit payments
        </p>
      </div>

      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          Direct Debit mandates must be set up in the GoCardless dashboard. This page allows you to
          link existing GoCardless customers and mandates to your Odin customers.
        </AlertDescription>
      </Alert>

      {/* Error State */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading mandate mappings...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Card */}
      {!isLoading && summary && (
        <Card>
          <CardHeader>
            <CardTitle>Mapping Summary</CardTitle>
            <CardDescription>Overview of GoCardless mandate linkages</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <div className="text-2xl font-bold">{summary.total}</div>
                <div className="text-xs text-muted-foreground">Total Customers</div>
              </div>
              <div className="space-y-1">
                <div className="text-2xl font-bold text-green-600">{summary.mapped}</div>
                <div className="text-xs text-muted-foreground">Linked</div>
              </div>
              <div className="space-y-1">
                <div className="text-2xl font-bold text-yellow-600">{summary.unmapped}</div>
                <div className="text-xs text-muted-foreground">Unlinked</div>
              </div>
              <div className="space-y-1">
                <div className="text-2xl font-bold">{summary.percentage}%</div>
                <div className="text-xs text-muted-foreground">Coverage</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unmapped Customers Section */}
      {!isLoading && unmappedCustomers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              Unlinked Customers ({unmappedCustomers.length})
            </CardTitle>
            <CardDescription>
              Customers with GoCardless payment method that haven't been linked to a mandate yet
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Suggestions</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unmappedCustomers.map((customer) => {
                  const customerSuggestions = suggestions[customer.id] || []
                  const topSuggestion = customerSuggestions[0]

                  return (
                    <TableRow key={customer.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{customer.name}</div>
                          {customer.email && (
                            <div className="text-xs text-muted-foreground">{customer.email}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {customer.branches?.[0]?.customer_type?.name || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {topSuggestion ? (
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-3 w-3 text-primary" />
                            <div className="text-xs">
                              <div className="font-medium">{topSuggestion.gcCustomerName}</div>
                              <div className="text-muted-foreground">
                                {Math.round(topSuggestion.confidence * 100)}% match
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            No suggestions
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => handleLinkCustomer(customer)}
                        >
                          <Link2 className="h-4 w-4 mr-2" />
                          Link
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Mapped Customers Section - Collapsed by default */}
      {!isLoading && mappings.length > 0 && (
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="mapped-customers" className="border rounded-lg">
            <Card className="border-0">
              <CardHeader className="pb-0">
                <AccordionTrigger className="hover:no-underline py-6">
                  <div className="flex flex-col items-start text-left">
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      Linked Customers ({mappings.length})
                    </CardTitle>
                    <CardDescription>
                      Customers successfully linked to GoCardless mandates
                    </CardDescription>
                  </div>
                </AccordionTrigger>
              </CardHeader>
              <AccordionContent>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>GoCardless Details</TableHead>
                        <TableHead>Mandate</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Linked</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mappings.map((mapping) => (
                        <TableRow key={mapping.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{mapping.customers?.name || 'Unknown'}</div>
                              {mapping.customers?.email && (
                                <div className="text-xs text-muted-foreground">
                                  {mapping.customers.email}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs">
                              <div className="font-mono text-muted-foreground">
                                {mapping.gocardless_customer_id}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs">
                              <div className="font-mono">{mapping.gocardless_mandate_id}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={getMandateStatusBadgeVariant(mapping.gocardless_mandate_status)}>
                              {formatMandateStatus(mapping.gocardless_mandate_status)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs text-muted-foreground">
                              {new Date(mapping.mapped_at).toLocaleDateString()}
                            </div>
                            {mapping.mapping_type === 'auto_matched' && mapping.confidence_score && (
                              <div className="text-xs text-muted-foreground">
                                Auto ({Math.round(mapping.confidence_score * 100)}%)
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                handleUnlinkCustomer(
                                  mapping.odin_customer_id,
                                  mapping.customers?.name || 'Unknown'
                                )
                              }
                            >
                              <Unlink className="h-4 w-4 mr-2" />
                              Unlink
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </AccordionContent>
            </Card>
          </AccordionItem>
        </Accordion>
      )}

      {/* Empty State - Compact */}
      {!isLoading && mappings.length === 0 && unmappedCustomers.length === 0 && (
        <Card>
          <CardContent className="py-6">
            <div className="flex items-center gap-3 text-sm">
              <Info className="h-6 w-6 text-muted-foreground/50 shrink-0" />
              <div>
                <p className="font-medium">No GoCardless customers yet</p>
                <p className="text-muted-foreground text-xs mt-0.5">
                  Set customer payment method to GoCardless to enable mandate linking
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Link Modal */}
      <GoCardlessMatchingModal
        open={isLinkModalOpen}
        onClose={() => {
          setIsLinkModalOpen(false)
          setSelectedCustomer(null)
        }}
        customer={selectedCustomer}
        onSuccess={handleLinkSuccess}
      />
    </div>
  )
}
